import {
  Box,
  ComboboxData,
  Menu,
  Select,
  Text,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import Popover from "@/components/widgets/Popover/Popover";

import type { DropdownItem } from "@/components/elements/Dropdown/types";
import type { Dispatch, FC, SetStateAction } from "react";

interface Props {
  kpisDisabled: boolean;
  showConditionsTooltip: boolean;
  selectValue: string | undefined;
  displayedValue: string | undefined;
  dropdownBenchmarks: DropdownItem[];
  setBenchmark: Dispatch<SetStateAction<string | undefined>>;
  currentBenchmark: string | undefined;
}

const MobileConditionsSelector: FC<Props> = ({
  kpisDisabled,
  showConditionsTooltip,
  selectValue,
  displayedValue,
  dropdownBenchmarks,
  setBenchmark,
  currentBenchmark,
}) => {
  const { t } = useTranslation();
  const [popoverOpened, setPopoverOpened] = useState(false);

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  return (
    <Popover
      title={t("components.PerformanceChart.selectCondition")}
      actionInPending={false}
      popoverOpened={popoverOpened}
      setPopoverOpened={setPopoverOpened}
      target={
        <Tooltip
          label={t("plans.contentLocker.upgradeToAccess")}
          disabled={!kpisDisabled || !showConditionsTooltip}
          withArrow
          events={{ hover: true, focus: true, touch: true }}
          position="top"
          offset={-8}
          maw={300}
          openDelay={100}
          multiline
        >
          <Box style={{ cursor: "pointer" }} onClick={() => setPopoverOpened((prev) => !prev)}>
            <Select
              disabled={kpisDisabled}
              label={t("components.PerformanceChart.conditions")}
              value={selectValue}
              data={dropdownBenchmarks as ComboboxData}
              readOnly
              w={200}
              placeholder={displayedValue}
            />
          </Box>
        </Tooltip>
      }
    >
      <Menu>
        {dropdownBenchmarks.map((item) => (
          <Menu.Item
            key={`benchmark-${item.value}`}
            onClick={() => {
              setBenchmark(item.value);
              setPopoverOpened(false);
            }}
            style={{
              cursor: "pointer",
              padding: "var(--mantine-spacing-sm)",
              borderRadius: "var(--mantine-radius-default)",
              backgroundColor:
                item.value === currentBenchmark
                  ? isDark
                    ? "var(--mantine-color-dark-4)"
                    : "var(--mantine-color-gray-0)"
                  : "transparent",
              "&:hover": {
                backgroundColor: isDark
                  ? "var(--mantine-color-dark-4)"
                  : "var(--mantine-color-gray-0)",
              },
            }}
          >
            <Text size="sm">{item.label}</Text>
          </Menu.Item>
        ))}
      </Menu>
    </Popover>
  );
};

export default MobileConditionsSelector;
